import { z } from 'zod'

import type { Money } from '@/lib/money'
import { lenientList, moneySchema } from '@/schemas/common'

/**
 * The Advanced screener: `GET /api/v5/screener/metrics/` (what can be shown, filtered and sorted
 * on) and `GET /api/v5/screener/` (one page of the stocks that match).
 *
 * The metrics response is typed HERE, not off `@/api/schema`: the spec documents that path with
 * mm-be's sector-stats `Metric` (a serializer name clash on mm-be, backend ask B1), so the
 * generated type is wrong for it.
 */

/** What a metric's figure counts in. mm-be serves exactly these five today. */
export const METRIC_UNITS = ['npr', 'percent', 'times', 'shares', 'number'] as const

export type MetricUnit = (typeof METRIC_UNITS)[number]

/** A key as mm-be spells them, and the only shape that may reach a `min_`/`max_`/`sort` param. */
export const metricKeySchema = z.string().regex(/^[a-z][a-z0-9_]*$/)

export const metricSchema = z.object({
  key: metricKeySchema,
  /** As served ("P/E", "Change %"): the column head and the filter row's name. */
  label: z.string().min(1),
  /** The column set and the filter group it sits in: "overview", "enterprise_value". */
  group: z.string().min(1),
  /** A unit this build does not know is drawn as a plain number rather than dropping the metric. */
  unit: z.enum(METRIC_UNITS).catch('number'),
  /** One sentence: the head's and the filter row's term card. */
  description: z.string(),
})

export type Metric = z.infer<typeof metricSchema>

/** In display order, as served. One row that will not parse is dropped, not the panel. */
export const metricListSchema = lenientList(metricSchema, 'screener metric')

/**
 * One stock. `values` holds every metric figure the row carried, keyed as `/metrics/` keys them,
 * so a metric mm-be adds is a column here with no change to this file. Null is mm-be's "not
 * computed" (a financial's EV ratios, a stock that did not trade today), never a zero.
 */
export const screenerRowSchema = z.object({
  symbol: z.string().min(1),
  name: z.string().min(1),
  sectorSymbol: z.string().min(1).nullable(),
  sectorName: z.string().min(1).nullable(),
  values: z.record(metricKeySchema, moneySchema.nullable()),
})

export type ScreenerRow = z.infer<typeof screenerRowSchema>

export const screenerPageSchema = z.object({
  rows: lenientList(screenerRowSchema, 'screener'),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  total: z.number().int().min(0),
})

export type ScreenerPage = z.infer<typeof screenerPageSchema>

/** A sector a screen can be narrowed to: `/market/sectors/`'s key, which `sector=` takes. */
export const screenerSectorSchema = z.object({
  key: z.string().min(1),
  name: z.string().min(1),
})

export type ScreenerSector = z.infer<typeof screenerSectorSchema>

/** One metric's inclusive bounds. At least one is set: a range with neither is no filter. */
export type MetricRange =
  { readonly min: Money; readonly max: Money | null } | { readonly min: null; readonly max: Money }

/** The applied ranges, by metric key. */
export type MetricRanges = Readonly<Record<string, MetricRange>>

/** What narrows the list: the counts, the heatmap and the CSV all read this much. */
export interface ScreenFilter {
  readonly ranges: MetricRanges
  readonly sector: string | null
}

/** A sort key as `sort=` takes it: a metric key, `symbol` or `name`, `-` first for descending. */
export interface ScreenSort {
  readonly key: string
  readonly desc: boolean
}

/** One page of a screen: the filter, the order and the page. */
export interface ScreenQuery extends ScreenFilter {
  /** Null is mm-be's default order, by symbol. */
  readonly sort: ScreenSort | null
  readonly page: number
  readonly pageSize: number
}
